import { GetApp } from "../App"
import { SocketCallback, StreamCallback } from "./Client"
import { IAppState } from "../Types"
import { SubscriptionToken } from "../Types/Generated/LogbookApi/Subscriptions"
import { useClientSubscription } from "app-base/client/react"

// Subscribes using a typed `SubscriptionToken<T>` from the generated `Subscription`
// map (e.g. `Subscription.Day`, or the aliased `Subscription.dayNavigation`) instead
// of a hand-written wire string. The token's phantom `T` fixes the callback's payload
// type, and its `type` supplies the actual wire subscription key, so a callback can
// only ever be paired with the token's matching payload — mirroring the Swift-side
// `TypedBroadcastAlias`.
//
// `callback` never needs `useCallback` at the call site: a ref always holds the
// latest callback (kept current by the layout effect below on every render), so the
// subscribe/unsubscribe effect can run once on mount with an empty dependency array
// and still invoke the freshest closure.
export function useSubscription<T>(callback: StreamCallback<T>, token: SubscriptionToken<T>): void {
  const app = GetApp();
  useClientSubscription(app.socket, token.type, callback);
}

export function trigger<T>(app: IAppState, type: string, data: T, info?: string) {
  app.socket.trigger<T>(type, data, info || "trigger");
}

// Subscribes to the client-only socket connection event (connect/disconnect). This
// event is triggered locally by the Client rather than the server broadcast layer,
// so it is deliberately outside the typed `SubscriptionMap` and has its own hook.
export function useSocket(callback: SocketCallback): void {
  const app = GetApp();
  useClientSubscription(
    app.socket,
    "socket",
    (connected: boolean, _type: string, event: string) => callback(connected, event),
  );
}
